// The Inbox swipe, as pure rules (app-feel plan, builder B, 2026-10-01).
//
// Right = « Traité », left = « Ignorer », through the SAME inboxAct path and
// the same 6 s undo as the buttons. Three rules keep it from ever dropping a
// client by accident or fighting the rest of the app:
//
//   · EDGE      a touch that STARTS in the left 24 px is the shell's
//               swipe-back (shared contract) — never a card swipe.
//   · AXIS LOCK nothing is decided before ~10 px of travel; mostly-vertical
//               goes to the list's scroll, and the card never moves.
//   · THRESHOLD 35 % of the card (80–160 px). Short of it, the card springs
//               back and nothing is sent.

export const EDGE_PX = 24;
export const AXIS_LOCK_PX = 10;

export type Axis = 'x' | 'y';
export type SwipeAction = 'handled' | 'ignored';
export interface SwipeAllow { handle: boolean; ignore: boolean }

/** Does a touch starting at this x belong to the shell's swipe-back? */
export function inEdge(clientX: number, edge = EDGE_PX): boolean {
  return clientX < edge;
}

/** Which way the gesture goes, once the finger travelled far enough. A tie goes to the scroll. */
export function lockAxis(dx: number, dy: number, min = AXIS_LOCK_PX): Axis | null {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.max(ax, ay) < min) return null;
  return ax > ay ? 'x' : 'y';
}

/** How far the card must travel to mean it. */
export function swipeThreshold(width: number): number {
  return Math.min(160, Math.max(80, width * 0.35));
}

/** What a released swipe does — or null, and the card springs back. */
export function swipeOutcome(dx: number, width: number, allow: SwipeAllow): SwipeAction | null {
  if (Math.abs(dx) < swipeThreshold(width)) return null;
  if (dx > 0) return allow.handle ? 'handled' : null;
  return allow.ignore ? 'ignored' : null;
}

/** Where the card is drawn: under the finger, or resisting a direction it cannot go. */
export function dragOffset(dx: number, allow: SwipeAllow): number {
  const ok = dx > 0 ? allow.handle : allow.ignore;
  if (ok) return dx;
  // A rubber band: it moves a little, so the finger feels « not this way ».
  return Math.sign(dx) * Math.min(16, Math.sqrt(Math.abs(dx)) * 1.5);
}
